import { Show, createSignal, createMemo, onMount } from 'solid-js';
import { useAtTop, useStyleDetails, IStyleDetails, PathElement } from 'Shared/frontend/widgets/StylesProvider';
import { siteTheme } from 'Shared/frontend/SettingsProvider';
import { locationMatches } from 'Shared/frontend/widgets/locationMatches';
import { applyRule, resolveField } from 'Shared/frontend/widgets/Styles';
import { Location } from 'Common/Location';

/*
	Used to resolve styles for navs, footers, and headers should they exist.
	The elements all wrap the main content and so need to resolve at a different time 
	from the main page content.
*/

function resolveWrapStyles(styleDetails: IStyleDetails[], path:Location, atTop: boolean): any
{
	const pathElements: PathElement[] = path.map(key => ({key}));
	let css = {};
	let settings = {};

	for (const detail of styleDetails) {
		const style = siteTheme().styles[detail.category]?.[detail.tag];
		if (!style) continue;

		for (const rule of style.rules ?? []) {
			const location = resolveField(rule.location, detail.params, atTop);
			if (locationMatches([...detail.pathWhenAdded, ...location], pathElements))
				applyRule(rule, detail.params, atTop, css, settings);
		}
	}
	delete settings.location;
	return {...settings, css};
}

/* 
	The !mounted() test is required for these the page wrapper types to work when in SSR. 
	If omitted then elements that are meant to have been hidden via style rules will be displayed 
	when in SSR. I can't remember why exactly.

	Note that including the nav in SSR mode may also help web crawlers index links.
 */
export function ShowWrapElement(P:{children,path:Location})
{
	const atTop = useAtTop();
	const styleDetails = useStyleDetails();

	const [mounted, setMounted] = createSignal(false);
	onMount(() => setMounted(true));

	const styles = createMemo(() => resolveWrapStyles(styleDetails(), P.path, atTop()));

	/*
		Mirror the original pattern: always show before mount (SSR), after mount only hide if styles say to.

		Note: we pass the `styles` accessor (not styles()) to the children so that the wrapped
		element is created once and persists. If we passed styles() here, this expression would
		re-run whenever atTop flips, recreating the whole element from scratch and killing any
		CSS transition (eg the 'Fade in background' nav style would clunk in rather than fade).
		Children should read styles() in attribute positions so only the class updates in place.
	*/
	return (
		<Show when={!mounted() || !styles().hide}>
			{P.children(styles)}
		</Show>
	);
}

